import type { FunctionComponent, JSX } from "react";
import { useState } from "react";

import type React from "react";

import { Button, ButtonLabel, PopupClickContainerStateless, FilterNotification } from "@similarweb/ui-components";

import {
    Container,
    FilterNotificationWrapper,
    Footer,
    Text,
    Title,
    TitleContainer,
} from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipStyles";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { allTrackers } from "app/services/track/track";

interface IFilterChangedTooltipContentProps {
    title?: string;
    text: string;
    onClose?: (event: React.SyntheticEvent<HTMLElement>) => void;
    shouldPresentFooter?: boolean;
}
const FilterChangedTooltipContent: FunctionComponent<IFilterChangedTooltipContentProps> = ({
    title,
    text,
    onClose,
    shouldPresentFooter,
}) => {
    const translate = useTranslation();
    const onWrapperClick = (event: FixmeType) => {
        event.stopPropagation();
    };

    return (
        <Container onClick={onWrapperClick}>
            {title ? (
                <TitleContainer>
                    <Title>{title}</Title>
                </TitleContainer>
            ) : null}
            <Text>{text}</Text>
            {shouldPresentFooter ? (
                <Footer>
                    <Button
                        type={buttonTypes.INVERTED}
                        onClick={onClose}
                    >
                        <ButtonLabel>{translate("modal.no-touch.not-allowed.button")}</ButtonLabel>
                    </Button>
                </Footer>
            ) : null}
        </Container>
    );
};

const ChildElement = (
    onMouseEnter: React.MouseEventHandler<HTMLDivElement>,
    onMouseLeave: React.MouseEventHandler<HTMLDivElement>,
    shouldPresentEducationalTooltip: boolean,
) => (
    <FilterNotificationWrapper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        <FilterNotification
            width={"10px"}
            height={"10px"}
            animationOn={shouldPresentEducationalTooltip}
        />
    </FilterNotificationWrapper>
);

export interface IFilterChangedNotificationProps {
    preferencesKey: string;
    onClose?: VoidFunction;
    width?: number;
    firstTooltipTitle?: string;
    tooltipTitle: string;
    firstTooltipBody: string;
    tooltipBody: string;
    filterType?: number;
    onTooltipVisible?: VoidFunction;
    appendTo?: string;
    placement?: string;
    trackOnTooltipShow?: VoidFunction;
    trackOnTooltipClose?: VoidFunction;
    childComponent?: (
        onMouseEnter: React.MouseEventHandler<HTMLDivElement>,
        onMouseLeave: React.MouseEventHandler<HTMLDivElement>,
        shouldPresentEducationalTooltip: boolean,
    ) => JSX.Element;
}
export const FilterChangedNotification: FunctionComponent<IFilterChangedNotificationProps> = ({
    preferencesKey,
    onClose,
    width = 265,
    firstTooltipTitle,
    tooltipTitle,
    firstTooltipBody,
    tooltipBody,
    filterType,
    onTooltipVisible,
    childComponent = ChildElement,
    appendTo = "body",
    placement = "bottom",
    trackOnTooltipShow,
    trackOnTooltipClose,
}) => {
    const filterChangeEducationalTooltipPresented = PreferencesService.get(preferencesKey);
    const [isOpen, setIsOpen] = useState<boolean>(!filterChangeEducationalTooltipPresented);
    const [shouldPresentEducationalTooltip, setShouldPresentEducationalTooltip] = useState<boolean>(false);

    if (!filterChangeEducationalTooltipPresented && isOpen && !shouldPresentEducationalTooltip) {
        trackOnTooltipShow
            ? trackOnTooltipShow()
            : allTrackers.dispatchTrackEvent(
                  "Change Query Filter",
                  "open",
                  `Change Query Filter/Got It Pop Up/${filterType || ""}`,
              );
        setShouldPresentEducationalTooltip(true);
    }

    const onCloseClick = (event: FixmeType) => {
        event.stopPropagation();
        onTooltipVisible?.();
        setShouldPresentEducationalTooltip(false);
        if (event) {
            setIsOpen(false);
        }
        trackOnTooltipClose
            ? trackOnTooltipClose()
            : allTrackers.dispatchTrackEvent(
                  "Change Query Filter",
                  "close",
                  `Change Query Filter/Pop Up/${filterType || ""}`,
              );
        onClose?.();
    };
    const popupConfig = {
        placement,
        width,
        defaultOpen: true,
        cssClassContainer: !shouldPresentEducationalTooltip
            ? "Popup-element-wrapper--dark-mode Popup-element-wrapper"
            : "Popup-element-wrapper--notification-mode Popup-element-wrapper",
        cssClassContent: "Popup-content-no-padding",
        enabled: true,
    };
    const tooltipTextTitle = shouldPresentEducationalTooltip ? firstTooltipTitle : tooltipTitle;
    const tooltipTextBody = shouldPresentEducationalTooltip ? firstTooltipBody : tooltipBody;
    const onMouseEnter = () => {
        if (!shouldPresentEducationalTooltip) {
            setIsOpen(true);
            allTrackers.dispatchTrackEvent("Change Query Filter", "open", `Blue Dot/Appear/${filterType || ""}`);
        }
    };
    const onMouseLeave = () => {
        if (!shouldPresentEducationalTooltip) {
            setIsOpen(false);
        }
    };

    return (
        <PopupClickContainerStateless
            content={() => (
                <FilterChangedTooltipContent
                    title={tooltipTextTitle}
                    onClose={onCloseClick}
                    text={tooltipTextBody}
                    shouldPresentFooter={shouldPresentEducationalTooltip}
                />
            )}
            config={popupConfig}
            isOpen={isOpen}
            appendTo={appendTo}
        >
            {childComponent(onMouseEnter, onMouseLeave, shouldPresentEducationalTooltip)}
        </PopupClickContainerStateless>
    );
};
